import React, { useState, useEffect } from "react";
import LocationSearch from "../../widgets/LocationSearch";
import ImageUpload from "../../widgets/ImageUpload";
import DenominationPicker from "../ui/DenominationPicker";
import PhoneInput from "../ui/PhoneInput";
import { isValidE164 } from "../ui/phoneUtils";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";

const fieldInputClass =
  "w-full rounded-md border border-ui-border bg-ui-card px-3 py-2 text-sm text-ui-foreground placeholder:text-ui-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background";

const hasText = (value) => typeof value === "string" && value.trim().length > 0;

const hasGeocodedLocation = (value, latitude, longitude) =>
  hasText(value) && Number.isFinite(latitude) && Number.isFinite(longitude);

const getProfileValidationErrors = (form) => ({
  first_name: hasText(form.first_name) ? "" : "First name is required.",
  last_name: hasText(form.last_name) ? "" : "Last name is required.",
  location: hasGeocodedLocation(form.location, form.latitude, form.longitude)
    ? ""
    : "Please select a location from the dropdown results.",
});

const getOrganizationValidationErrors = (form) => ({
  name: hasText(form.name) ? "" : "Organization name is required.",
  address: hasGeocodedLocation(form.address, form.latitude, form.longitude)
    ? ""
    : "Please select an address from the dropdown results.",
  phone:
    hasText(form.phone) && !isValidE164(form.phone.trim())
      ? "Please enter a valid phone number."
      : "",
});

const hasValidationErrors = (errors) => Object.values(errors).some(Boolean);

/**
 * @param {Object} props
 * @param {Object} props.editForm
 * @param {function} props.setEditForm
 * @returns {JSX.Element}
 */
const ProfileEditLinks = ({ editForm, setEditForm }) => {
  const addLink = () => {
    setEditForm({
      ...editForm,
      links: [...(editForm.links || []), { label: "", url: "" }],
    });
  };

  const removeLink = (index) => {
    setEditForm({
      ...editForm,
      links: (editForm.links || []).filter((_, i) => i !== index),
    });
  };

  const updateLink = (index, field, value) => {
    const updatedLinks = [...(editForm.links || [])];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    setEditForm({
      ...editForm,
      links: updatedLinks,
    });
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-text-secondary">Links</span>
        <Button type="button" variant="outline" size="sm" onClick={addLink}>
          Add link
        </Button>
      </div>

      {editForm.links && Array.isArray(editForm.links) && editForm.links.length > 0 ? (
        <div className="space-y-3">
          {editForm.links.map((link, index) => (
            <div key={index} className="flex items-start gap-2">
              <div className="min-w-0 flex-1 space-y-2">
                <input
                  type="text"
                  placeholder="Label (e.g. Website, LinkedIn)"
                  value={link.label}
                  onChange={(e) => updateLink(index, "label", e.target.value)}
                  className={fieldInputClass}
                />
                <input
                  type="url"
                  placeholder="https://example.com"
                  value={link.url}
                  onChange={(e) => updateLink(index, "url", e.target.value)}
                  className={fieldInputClass}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeLink(index)}
                title="Remove link"
                className="shrink-0 text-delete hover:text-delete-hover"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-text-muted">No links yet. Add one with the button above.</p>
      )}
    </div>
  );
};

const ProfileEditField = ({ user, onSave, onCancel }) => {
    const [editForm, setEditForm] = useState({
        first_name: "",
        last_name: "",
        bio: "",
        pfp_url: "",
        pfp_key: "",
        mission_statement: "",
        location: "",
        latitude: null,
        longitude: null,
        links: []
    });
    const [saving, setSaving] = useState(false);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const validationErrors = saveAttempted ? getProfileValidationErrors(editForm) : {};

    // Initialize form with user data when component mounts or user changes
    useEffect(() => {
        if (user) {
            setEditForm({
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                bio: user.bio || "",
                pfp_url: user.pfp_url || "",
                pfp_key: user.pfp_key || "",
                mission_statement: user.mission_statement || "",
                location: user.location || "",
                latitude: user.latitude ?? null,
                longitude: user.longitude ?? null,
                links: user.links || []
            });
        }
    }, [user]);

    const handleEditChange = (e) => {
        setEditForm({
            ...editForm,
            [e.target.name]: e.target.value
        });
    };

    const handleSave = async () => {
        setSaveAttempted(true);
        const errors = getProfileValidationErrors(editForm);
        if (hasValidationErrors(errors)) {
          return;
        }

        setSaving(true);
        try {
            await onSave(editForm);
        } catch (error) {
            console.error("Error saving profile:", error);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset form to original user data
        setEditForm({
            first_name: user.first_name || "",
            last_name: user.last_name || "",
            bio: user.bio || "",
            pfp_url: user.pfp_url || "",
            pfp_key: user.pfp_key || "",
            mission_statement: user.mission_statement || "",
            location: user.location || "",
            latitude: user.latitude ?? null,
            longitude: user.longitude ?? null,
            links: user.links || []
        });
        setSaveAttempted(false);
        onCancel();
    };

  return (
    <Card className="shadow-profile-md">
      <CardHeader>
        <CardTitle className="text-xl text-text-primary">Edit profile</CardTitle>
        <Separator />
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <ImageUpload
            value={editForm.pfp_url}
            onChange={(imageData) => {
              setEditForm({
                ...editForm,
                pfp_url: imageData.url || imageData,
                pfp_key: imageData.key || "",
              });
            }}
            label="Profile picture"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="edit-first-name">
              First name <span className="text-error">*</span>
            </label>
            <input
              id="edit-first-name"
              type="text"
              name="first_name"
              value={editForm.first_name}
              onChange={handleEditChange}
              required
              className={cn(fieldInputClass, validationErrors.first_name && "border-error")}
            />
            {validationErrors.first_name && <p className="mt-1 text-sm text-error">{validationErrors.first_name}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="edit-last-name">
              Last name <span className="text-error">*</span>
            </label>
            <input
              id="edit-last-name"
              type="text"
              name="last_name"
              value={editForm.last_name}
              onChange={handleEditChange}
              required
              className={cn(fieldInputClass, validationErrors.last_name && "border-error")}
            />
            {validationErrors.last_name && <p className="mt-1 text-sm text-error">{validationErrors.last_name}</p>}
          </div>
        </div>

        <div>
          <span className="mb-1 block text-sm font-medium text-text-secondary">
            Location <span className="text-error">*</span>
          </span>
          <LocationSearch
            value={{
              value: editForm.location || "",
              lat: editForm.latitude,
              lon: editForm.longitude,
            }}
            onChange={(locationData) => {
              setEditForm({
                ...editForm,
                location: locationData.value,
                latitude: locationData.lat,
                longitude: locationData.lon,
              });
            }}
            placeholder="City, state, or country"
            searchType="city"
            required
            inputClassName={cn(validationErrors.location && "border-error")}
          />
          {validationErrors.location && <p className="mt-1 text-sm text-error">{validationErrors.location}</p>}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="edit-mission">
            Mission statement
          </label>
          <Textarea
            id="edit-mission"
            maxLength={500}
            name="mission_statement"
            rows={2}
            value={editForm.mission_statement}
            onChange={handleEditChange}
            className={cn(fieldInputClass, "min-h-[4rem] resize-y shadow-none")}
            placeholder="Share your mission or purpose…"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="edit-bio">
            Bio
          </label>
          <Textarea
            id="edit-bio"
            maxLength={5000}
            name="bio"
            rows={4}
            value={editForm.bio}
            onChange={handleEditChange}
            className={cn(fieldInputClass, "min-h-[6rem] resize-y shadow-none")}
            placeholder="Tell us about yourself…"
          />
        </div>

        <ProfileEditLinks editForm={editForm} setEditForm={setEditForm} />
      </CardContent>
      <CardFooter className="flex flex-wrap justify-end gap-2 border-t border-ui-border pt-6">
        <Button type="button" variant="outline" onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </CardFooter>
    </Card>
  );
};

const OrganizationProfileEditField = ({ organization, onSave, onCancel }) => {
    const [editForm, setEditForm] = useState({
        name: "",
        bio: "",
        mission_statement: "",
        website: "",
        phone: "",
        address: "",
        latitude: null,
        longitude: null,
        pfp_url: "",
        pfp_key: "",
        denomination: "",
        links: []
    });
    const [saving, setSaving] = useState(false);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const validationErrors = saveAttempted ? getOrganizationValidationErrors(editForm) : {};

    // Initialize form with organization data when component mounts or organization changes
    useEffect(() => {
        if (organization) {
            setEditForm({
                name: organization.name || "",
                bio: organization.bio || "",
                mission_statement: organization.mission_statement || "",
                website: organization.website || "",
                phone: organization.phone || "",
                address: organization.address || "",
                latitude: organization.latitude ?? null,
                longitude: organization.longitude ?? null,
                pfp_url: organization.pfp_url || "",
                pfp_key: organization.pfp_key || "",
                denomination: organization.denomination || "",
                links: organization.links || []
            });
        }
    }, [organization]);

    const handleEditChange = (e) => {
        setEditForm({
            ...editForm,
            [e.target.name]: e.target.value
        });
    };

    const handleSave = async () => {
        setSaveAttempted(true);
        const errors = getOrganizationValidationErrors(editForm);
        if (hasValidationErrors(errors)) {
          return;
        }

        setSaving(true);
        try {
            await onSave(editForm);
        } catch (error) {
            console.error("Error saving organization:", error);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset form to original organization data
        setEditForm({
            name: organization.name || "",
            bio: organization.bio || "",
            mission_statement: organization.mission_statement || "",
            website: organization.website || "",
            phone: organization.phone || "",
            address: organization.address || "",
            latitude: organization.latitude ?? null,
            longitude: organization.longitude ?? null,
            pfp_url: organization.pfp_url || "",
            pfp_key: organization.pfp_key || "",
            denomination: organization.denomination || "",
            links: organization.links || []
        });
        setSaveAttempted(false);
        onCancel();
    };

  return (
    <Card className="shadow-profile-md">
      <CardHeader>
        <CardTitle className="text-xl text-text-primary">Edit organization</CardTitle>
        <Separator />
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <ImageUpload
            value={editForm.pfp_url}
            onChange={(imageData) => {
              setEditForm({
                ...editForm,
                pfp_url: imageData.url,
                pfp_key: imageData.key,
              });
            }}
            label="Organization logo"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="org-denomination">
            Denomination
          </label>
          <DenominationPicker
            id="org-denomination"
            value={editForm.denomination || ""}
            onChange={(denomination) => setEditForm({ ...editForm, denomination })}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="org-name">
            Organization name <span className="text-error">*</span>
          </label>
          <input
            id="org-name"
            type="text"
            name="name"
            value={editForm.name}
            onChange={handleEditChange}
            required
            className={cn(fieldInputClass, validationErrors.name && "border-error")}
            placeholder="Enter organization name"
          />
          {validationErrors.name && <p className="mt-1 text-sm text-error">{validationErrors.name}</p>}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="org-mission">
            Mission statement
          </label>
          <Textarea
            id="org-mission"
            maxLength={500}
            name="mission_statement"
            rows={2}
            value={editForm.mission_statement}
            onChange={handleEditChange}
            className={cn(fieldInputClass, "min-h-[4rem] resize-y shadow-none")}
            placeholder={"Share your organization's mission…"}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="org-bio">
            Description
          </label>
          <Textarea
            id="org-bio"
            maxLength={5000}
            name="bio"
            rows={4}
            value={editForm.bio}
            onChange={handleEditChange}
            className={cn(fieldInputClass, "min-h-[6rem] resize-y shadow-none")}
            placeholder="Tell us about your organization…"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="org-phone">
            Phone
          </label>
          <PhoneInput
            id="org-phone"
            value={editForm.phone}
            invalid={Boolean(validationErrors.phone)}
            onChange={(phone) => setEditForm({ ...editForm, phone })}
          />
          {validationErrors.phone ? (
            <p className="mt-1 text-sm text-error">{validationErrors.phone}</p>
          ) : null}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary" htmlFor="org-website">
            Website
          </label>
          <input
            id="org-website"
            type="url"
            name="website"
            value={editForm.website}
            onChange={handleEditChange}
            className={fieldInputClass}
            placeholder="https://www.organization.com"
          />
        </div>

        <div>
          <span className="mb-1 block text-sm font-medium text-text-secondary">
            Address <span className="text-error">*</span>
          </span>
          <LocationSearch
            value={{
              value: editForm.address || "",
              lat: editForm.latitude,
              lon: editForm.longitude,
            }}
            onChange={(addressData) => {
              setEditForm({
                ...editForm,
                address: addressData.value,
                latitude: addressData.lat,
                longitude: addressData.lon,
              });
            }}
            placeholder="Search for an address..."
            searchType="address"
            required
            inputClassName={cn(validationErrors.address && "border-error")}
          />
          {validationErrors.address && <p className="mt-1 text-sm text-error">{validationErrors.address}</p>}
        </div>

        <ProfileEditLinks editForm={editForm} setEditForm={setEditForm} />
      </CardContent>
      <CardFooter className="flex flex-wrap justify-end gap-2 border-t border-ui-border pt-6">
        <Button type="button" variant="outline" onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export { ProfileEditField, OrganizationProfileEditField };
